import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@/contexts/UserContext';
import { getToken } from '@/utils/localStorage';
import { ArrowLeft, ArrowRight, Check, CheckCircle, BookOpen, Users, Brain, Heart } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Story Excavation: 90 Journal Prompts organized by 4 Sections
 * Sections group prompts by perspective:
 *   1. Self About Self       — how you experience your own inner life
 *   2. Self About Others     — how you perceive & react to others
 *   3. Other About Self      — how others' perceptions have shaped you
 *   4. Others About Others   — the tonal rules & dynamics you witnessed
 * Prompts are hidden until the user completes them one by one.
 */
const SECTIONS = [
  {
    id: 1,
    name: "Self About Self",
    subtitle: "How you experience your own inner life.",
    icon: Brain,
    color: "burnt-terra",
    prompts: [
      "What role earned you safety: the quiet one, the funny one, the strong one?",
      "When did you first learn that tone mattered more than truth?",
      "How did your body adapt to stay acceptable?",
      "What tone kept you included?",
      "If your childhood self were a character, what was their title?",
      "What tone do you default to under pressure?",
      "Where did that tone first keep you safe?",
      "What posture accompanies that tone?",
      "What story does your body tell before you speak?",
      "What situations trigger the old role automatically?",
      "Which reactions feel immediate and involuntary?",
      "What brick from the past shows up most often?",
      "What outcome does that brick reliably produce?",
      "What chapter repeats the most?",
      "What thought undermines you before action?",
      "When did that narration begin?",
      "What is it trying to prevent?",
      "What does it cost you now?",
      "What would change if that narrator were demoted?",
      "When did your tone override your intent?",
      "Where did your tone protect you?",
      "Where did it cost you authority?",
      "What pattern do you now see clearly?",
      "Where do you brace when uncertain?",
      "Where do you collapse when challenged?",
      "What happens to your breath when you disagree?",
      "What posture signals \"don't notice me\"?",
      "What posture signals \"don't cross me\"?",
      "When do you rush instead of occupy space?",
      "Where does your voice thin out?",
      "What would your body do if the story changed?",
      "What moment usually triggers the old script?",
      "What sensation precedes it?",
      "What pause would break the sequence?",
      "What tone would alter the trajectory?",
      "What does \"quiet authority\" feel like physically?",
      "How would slowing your voice change the scene?",
      "What happens if you exhale before responding?",
      "What if you let silence work for you?",
      "Where can you test this safely?",
      "How could curiosity replace defense?",
      "What tone supports the person you are becoming?",
      "What posture matches that role?",
      "What belief must be retired?",
      "What brick from your past becomes a strength?",
      "What does authority look like without force?",
      "What does warmth look like without collapse?",
      "What would consistency feel like?",
      "What is the title of this new chapter?",
      "What daily behavior reinforces this new story?",
      "What tone will you practice today?",
      "What physical cue anchors it?",
      "Where will resistance appear?",
      "How will you recognize regression early?",
      "What evidence will tell you this is working?",
      "What will you stop doing?",
      "What is the opening line of tomorrow's scene?"
    ]
  },
  {
    id: 2,
    name: "Self About Others",
    subtitle: "How you have perceived, read, and reacted to others.",
    icon: Users,
    color: "shadow-pine",
    prompts: [
      "Did authority speak with certainty or volatility?",
      "Was \"no\" delivered as protection or threat?",
      "Whose moods controlled the room?",
      "Where did warmth change the result?",
      "Where did sharpness close doors?",
      "What relationships were shaped by misunderstanding?",
      "What tone invites trust?",
      "What tone creates distance?",
      "When have you misread someone else's tone?",
      "What story did you project onto them?",
      "Who deserves a revised interaction?",
      "What new outcome is possible?"
    ]
  },
  {
    id: 3,
    name: "Other About Self",
    subtitle: "How others' perceptions and reactions have shaped you.",
    icon: Heart,
    color: "burnt-terra",
    prompts: [
      "In your earliest memories, who were you required to be?",
      "What happened when you spoke \"out of character\"?",
      "What emotion were you not allowed to express?",
      "What tone got you punished?",
      "What does your voice do when you feel evaluated?",
      "Whose voice critiques you when you hesitate?",
      "What tone does your inner voice use?",
      "Is that tone deserved?",
      "When did silence get mistaken for consent?",
      "What opportunities were lost to misread tone?",
      "What physical habit betrays your confidence?",
      "Who experiences you as warm?",
      "Who experiences you as guarded?",
      "What role are you no longer willing to play?",
      "Who will feel the difference first?"
    ]
  },
  {
    id: 4,
    name: "Others About Others",
    subtitle: "The tonal rules and dynamics you witnessed around you.",
    icon: BookOpen,
    color: "shadow-pine",
    prompts: [
      "Was silence rewarded or required?",
      "What tone ended conflict fastest?",
      "What tone escalated it?",
      "What happened when someone challenged the emotional weather?",
      "What voices were considered legitimate?",
      "What tones were dangerous?"
    ]
  }
];

// Calculate total prompts
const TOTAL_PROMPTS = SECTIONS.reduce((sum, section) => sum + section.prompts.length, 0);

const ENTRY_STATES = {
  PROMPT: 'prompt',        // showing the prompt + reflection line, "Start reflection" button
  REFLECTING: 'reflecting', // 2-minute countdown timer running
  WRITING: 'writing',      // countdown done, user writes in their journal, "Mark complete" button
};

const REFLECTION_SECS = 120; // 2 minutes

const CLAY = '#C4440A';
const CREAM = '#F5F2EC';
const BG = '#1A1814';
const CARD = '#252018';

const ExcavationWriting = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [selectedSectionId, setSelectedSectionId] = useState(null); // null = show section picker
  const [currentPromptIndex, setCurrentPromptIndex] = useState(0); // 0-based within selected section
  const [entries, setEntries] = useState({}); // key: `${sectionId}_${promptIndex}`
  const [showIntro, setShowIntro] = useState(true);
  const [entryState, setEntryState] = useState(ENTRY_STATES.PROMPT);
  const [reflectionRemaining, setReflectionRemaining] = useState(REFLECTION_SECS);
  const [saving, setSaving] = useState(false);
  const timerRef = useRef(null);

  // Resolve which section + promptIndex an old sequential prompt-number belonged to.
  // Used to migrate legacy entries keyed by 1..90 into the new `${sectionId}_${idx}` form.
  const resolveLegacyPrompt = (promptNumber) => {
    let count = 0;
    for (const section of SECTIONS) {
      if (promptNumber <= count + section.prompts.length) {
        return { sectionId: section.id, promptIndex: promptNumber - count - 1 };
      }
      count += section.prompts.length;
    }
    return null;
  };

  // Load saved progress (migrating legacy sequential keys if needed).
  useEffect(() => {
    try {
      const saved = localStorage.getItem('excavation90Entries');
      if (saved) {
        const parsed = JSON.parse(saved);
        const keys = Object.keys(parsed);
        const isLegacy = keys.some((k) => /^\d+$/.test(k));
        if (isLegacy) {
          const migrated = {};
          for (const k of keys) {
            const info = resolveLegacyPrompt(parseInt(k, 10));
            if (info) {
              migrated[`${info.sectionId}_${info.promptIndex}`] = {
                ...parsed[k],
                section: info.sectionId,
                promptIndex: info.promptIndex,
              };
            }
          }
          setEntries(migrated);
          localStorage.setItem('excavation90Entries', JSON.stringify(migrated));
        } else {
          setEntries(parsed);
        }
      }
      const introSeen = localStorage.getItem('excavation90IntroSeen');
      if (introSeen) setShowIntro(false);
    } catch (e) {
      // ignore
    }
  }, []);

  // Cleanup any running timer on unmount / prompt change
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  // Resolve current section + prompt text based on selectedSectionId + currentPromptIndex.
  const currentSection = selectedSectionId
    ? SECTIONS.find((s) => s.id === selectedSectionId)
    : null;
  const currentInfo = currentSection
    ? {
        section: currentSection,
        promptIndex: currentPromptIndex,
        prompt: currentSection.prompts[currentPromptIndex],
      }
    : null;

  const completedCount = Object.keys(entries).length;
  const progressPercent = Math.round((completedCount / TOTAL_PROMPTS) * 100);

  // Section-level progress helpers
  const sectionCompletedCount = (sectionId) => {
    const total = SECTIONS.find((s) => s.id === sectionId)?.prompts.length || 0;
    let done = 0;
    for (let i = 0; i < total; i += 1) {
      if (entries[`${sectionId}_${i}`]) done += 1;
    }
    return done;
  };

  const enterSection = (sectionId) => {
    const section = SECTIONS.find((s) => s.id === sectionId);
    if (!section) return;
    const firstUncompleted = section.prompts.findIndex(
      (_, i) => !entries[`${sectionId}_${i}`]
    );
    setSelectedSectionId(sectionId);
    setCurrentPromptIndex(firstUncompleted === -1 ? 0 : firstUncompleted);
    setEntryState(ENTRY_STATES.PROMPT);
    setReflectionRemaining(REFLECTION_SECS);
  };

  const backToSections = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setSelectedSectionId(null);
    setEntryState(ENTRY_STATES.PROMPT);
    setReflectionRemaining(REFLECTION_SECS);
  };

  const startReflection = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setReflectionRemaining(REFLECTION_SECS);
    setEntryState(ENTRY_STATES.REFLECTING);
    const startedAt = Date.now();
    timerRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startedAt) / 1000);
      const rem = Math.max(0, REFLECTION_SECS - elapsed);
      setReflectionRemaining(rem);
      if (rem <= 0) {
        clearInterval(timerRef.current);
        timerRef.current = null;
        setEntryState(ENTRY_STATES.WRITING);
      }
    }, 250);
  };

  const skipReflection = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setEntryState(ENTRY_STATES.WRITING);
  };

  const cancelReflection = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setReflectionRemaining(REFLECTION_SECS);
    setEntryState(ENTRY_STATES.PROMPT);
  };

  const markComplete = async () => {
    if (!currentInfo) return;
    setSaving(true);
    const now = new Date().toISOString();
    const key = `${currentInfo.section.id}_${currentInfo.promptIndex}`;
    const newEntries = {
      ...entries,
      [key]: {
        text: '',  // user writes in a physical journal — we only record that they completed the day
        completedAt: now,
        section: currentInfo.section.id,
        promptIndex: currentInfo.promptIndex,
      },
    };
    setEntries(newEntries);
    localStorage.setItem('excavation90Entries', JSON.stringify(newEntries));

    // Best-effort backend save (endpoint may not exist yet — ignore errors)
    try {
      const token = getToken();
      if (token) {
        await fetch(`${API_URL}/api/excavation/save`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            sectionId: currentInfo.section.id,
            promptIndex: currentInfo.promptIndex,
            entry: '',
          }),
        });
      }
    } catch (e) {
      // ignore
    }

    setSaving(false);

    // Advance: find next uncompleted prompt in this section. If none, drop
    // back to the section-picker so the user can pick another perspective.
    const section = currentInfo.section;
    const nextIdx = section.prompts.findIndex(
      (_, i) => i > currentInfo.promptIndex && !newEntries[`${section.id}_${i}`]
    );
    if (nextIdx === -1) {
      // Try wrapping to earlier uncompleted (skipped) prompts within the section
      const wrapIdx = section.prompts.findIndex(
        (_, i) => !newEntries[`${section.id}_${i}`]
      );
      if (wrapIdx === -1) {
        // Section fully complete — return to picker
        setSelectedSectionId(null);
      } else {
        setCurrentPromptIndex(wrapIdx);
      }
    } else {
      setCurrentPromptIndex(nextIdx);
    }
    setEntryState(ENTRY_STATES.PROMPT);
    setReflectionRemaining(REFLECTION_SECS);
  };

  const fmtTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  // ---------- INTRO ----------
  if (showIntro) {
    return (
      <div className="min-h-screen flex flex-col" style={{ background: BG, color: CREAM }} data-testid="excavation-intro">
        <div className="border-b px-6 py-4" style={{ borderColor: 'rgba(245,242,236,0.08)' }}>
          <button
            onClick={() => navigate('/client')}
            className="inline-flex items-center gap-2 text-[13px] text-[#F5F2EC]/50 hover:text-[#F5F2EC] transition-colors"
            data-testid="excavation-back"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Hub
          </button>
        </div>
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="max-w-2xl w-full text-center">
            <div className="text-[10px] font-medium tracking-[0.25em] uppercase text-[#F5F2EC]/65 mb-3">
              Story Archaeology
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl leading-tight mb-6">
              Excavation <em className="italic text-[#C4440A]">Writing</em>
            </h1>
            <p className="text-[15px] leading-relaxed text-[#F5F2EC]/70 max-w-xl mx-auto mb-8">
              90 prompts across 4 sections — grouped by perspective: Self About Self, Self About Others, Other About Self, and Others About Others. One prompt at a time. You&apos;ll pause, reflect, and then write by hand in your journal. When you finish, mark the day complete and the next prompt unlocks.
            </p>
            <div className="rounded-2xl border p-6 text-left mb-8" style={{ background: CARD, borderColor: 'rgba(245,242,236,0.08)' }}>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#F5F2EC]/65 mb-3">The daily rhythm</div>
              <ol className="space-y-3 text-[14px] text-[#F5F2EC]/85">
                <li className="flex gap-3">
                  <span className="text-[#C4440A] font-mono">01</span>
                  <span>Read today&apos;s prompt.</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-[#C4440A] font-mono">02</span>
                  <span>Start the 2-minute reflection. Pause, reflect on the question, notice what you feel in your body.</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-[#C4440A] font-mono">03</span>
                  <span>When the timer completes, write in your physical journal.</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-[#C4440A] font-mono">04</span>
                  <span>Mark the day complete when you&apos;re done.</span>
                </li>
              </ol>
            </div>
            <button
              onClick={() => {
                localStorage.setItem('excavation90IntroSeen', '1');
                setShowIntro(false);
              }}
              className="inline-flex items-center gap-2 px-8 py-3 rounded-full font-medium transition-colors"
              style={{ background: CREAM, color: BG }}
              data-testid="excavation-begin"
            >
              Begin excavation <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- ALL 90 PROMPTS COMPLETE ----------
  if (completedCount >= TOTAL_PROMPTS) {
    return (
      <div className="min-h-screen flex flex-col" style={{ background: BG, color: CREAM }} data-testid="excavation-complete">
        <div className="border-b px-6 py-4" style={{ borderColor: 'rgba(245,242,236,0.08)' }}>
          <button
            onClick={() => navigate('/client')}
            className="inline-flex items-center gap-2 text-[13px] text-[#F5F2EC]/50 hover:text-[#F5F2EC] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Hub
          </button>
        </div>
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="max-w-lg text-center">
            <div className="w-20 h-20 mx-auto mb-8 rounded-full flex items-center justify-center" style={{ background: 'rgba(196,68,10,0.15)' }}>
              <CheckCircle className="w-10 h-10 text-[#C4440A]" />
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl leading-tight mb-4">
              All 90 prompts <em className="italic text-[#C4440A]">excavated.</em>
            </h2>
            <p className="text-[15px] text-[#F5F2EC]/70 leading-relaxed mb-8">
              You&apos;ve moved through every section of your story. Your journal holds the archaeology of your voice.
            </p>
            <button
              onClick={() => navigate('/client')}
              className="px-8 py-3 rounded-full font-medium transition-colors"
              style={{ background: CREAM, color: BG }}
            >
              Return to Hub
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- SECTION PICKER ----------
  if (!selectedSectionId) {
    return (
      <div className="min-h-screen flex flex-col" style={{ background: BG, color: CREAM }} data-testid="excavation-picker">
        {/* Topbar */}
        <div className="border-b px-6 py-4 flex items-center justify-between pt-safe" style={{ borderColor: 'rgba(245,242,236,0.08)' }}>
          <button
            onClick={() => navigate('/client')}
            className="inline-flex items-center gap-2 text-[13px] text-[#F5F2EC]/50 hover:text-[#F5F2EC] transition-colors"
            data-testid="excavation-picker-back"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Hub
          </button>
          <div className="font-serif text-sm text-[#F5F2EC]/70">Story Archaeology</div>
          <div className="text-xs text-[#F5F2EC]/65 tabular-nums" data-testid="excavation-total-progress">
            {completedCount} / {TOTAL_PROMPTS}
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-1" style={{ background: 'rgba(245,242,236,0.05)' }}>
          <div className="h-full transition-all duration-500" style={{ background: CLAY, width: `${progressPercent}%` }} />
        </div>

        {/* Section grid */}
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="max-w-4xl w-full">
            <div className="text-center mb-10">
              <div className="text-[10px] font-medium tracking-[0.25em] uppercase text-[#F5F2EC]/65 mb-3">
                Choose your lens
              </div>
              <h1 className="font-serif text-3xl sm:text-4xl leading-tight mb-3">
                Which story <em className="italic text-[#C4440A]">are you writing</em> today?
              </h1>
              <p className="text-[14px] text-[#F5F2EC]/60 max-w-xl mx-auto">
                Each section holds a different perspective. Pick one and enter — you can return to this screen anytime.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="excavation-section-grid">
              {SECTIONS.map((section) => {
                const Icon = section.icon;
                const done = sectionCompletedCount(section.id);
                const total = section.prompts.length;
                const pct = Math.round((done / total) * 100);
                const isComplete = done >= total;
                return (
                  <button
                    key={section.id}
                    onClick={() => enterSection(section.id)}
                    data-testid={`excavation-section-card-${section.id}`}
                    className="text-left rounded-2xl border p-6 transition-all duration-200 hover:-translate-y-0.5 hover:border-[#C4440A]/40"
                    style={{ background: CARD, borderColor: 'rgba(245,242,236,0.08)' }}
                  >
                    <div className="flex items-start gap-4 mb-4">
                      <div
                        className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
                        style={{ background: 'rgba(196,68,10,0.15)' }}
                      >
                        <Icon className="w-5 h-5 text-[#C4440A]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-serif text-xl leading-tight text-[#F5F2EC] mb-1">
                          {section.name}
                        </h3>
                        <p className="text-[12.5px] text-[#F5F2EC]/60 leading-relaxed">
                          {section.subtitle}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-5">
                      <div className="flex-1 h-1 rounded-full mr-4" style={{ background: 'rgba(245,242,236,0.06)' }}>
                        <div className="h-full rounded-full transition-all" style={{ background: isComplete ? '#5DCAA5' : CLAY, width: `${pct}%` }} />
                      </div>
                      <div className="text-[11px] tabular-nums tracking-wider" style={{ color: isComplete ? '#5DCAA5' : 'rgba(245,242,236,0.55)' }}>
                        {isComplete ? 'Complete' : `${done} / ${total}`}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---------- DAILY PROMPT ----------
  return (
    <div className="min-h-screen flex flex-col" style={{ background: BG, color: CREAM }} data-testid="excavation-writing">
      {/* Topbar */}
      <div className="border-b px-6 py-4 flex items-center justify-between pt-safe" style={{ borderColor: 'rgba(245,242,236,0.08)' }}>
        <button
          onClick={backToSections}
          className="inline-flex items-center gap-2 text-[13px] text-[#F5F2EC]/50 hover:text-[#F5F2EC] transition-colors"
          data-testid="excavation-back-to-sections"
        >
          <ArrowLeft className="w-4 h-4" /> Back to sections
        </button>
        <div className="font-serif text-sm text-[#F5F2EC]/70">Story Archaeology</div>
        <button
          onClick={() => navigate('/client')}
          className="text-[13px] text-[#F5F2EC]/50 hover:text-[#F5F2EC] transition-colors"
          data-testid="excavation-back"
        >
          Hub
        </button>
      </div>

      {/* Progress bar */}
      <div className="h-1" style={{ background: 'rgba(245,242,236,0.05)' }}>
        <div
          className="h-full transition-all duration-500"
          style={{ background: CLAY, width: `${progressPercent}%` }}
        />
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="max-w-2xl w-full">
          {/* Section badge — no numbering */}
          {currentInfo && (
            <div className="mb-6 text-center">
              <div className="text-[10px] font-medium tracking-[0.25em] uppercase text-[#F5F2EC]/65 mb-1">
                {currentInfo.section.name}
              </div>
              <div className="text-[11px] text-[#F5F2EC]/50 italic">
                {currentInfo.section.subtitle}
              </div>
            </div>
          )}

          {/* Prompt card */}
          <div
            className="rounded-2xl border p-8 sm:p-10 mb-6 text-center"
            style={{ background: CARD, borderColor: 'rgba(245,242,236,0.08)' }}
            data-testid="excavation-prompt-card"
          >
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#F5F2EC]/65 mb-4">
              Today&apos;s prompt
            </div>
            <p
              className="font-serif text-2xl sm:text-[28px] leading-snug text-[#F5F2EC] mb-6"
              data-testid="excavation-prompt-text"
            >
              {currentInfo?.prompt}
            </p>

            {/* PROMPT state — invite to start reflection */}
            {entryState === ENTRY_STATES.PROMPT && (
              <>
                <div className="border-t pt-6 mt-2" style={{ borderColor: 'rgba(245,242,236,0.08)' }}>
                  <p
                    className="text-[15px] italic text-[#F5F2EC]/70 leading-relaxed mb-6"
                    data-testid="excavation-reflection-line"
                  >
                    Pause, reflect on the question, notice what you feel in your body.
                  </p>
                  <button
                    onClick={startReflection}
                    className="inline-flex items-center gap-2 px-8 py-3 rounded-full font-medium transition-colors"
                    style={{ background: CREAM, color: BG }}
                    data-testid="excavation-start-btn"
                  >
                    Start reflection · 2 min
                  </button>
                </div>
              </>
            )}

            {/* REFLECTING state — countdown */}
            {entryState === ENTRY_STATES.REFLECTING && (
              <div className="border-t pt-6 mt-2" style={{ borderColor: 'rgba(245,242,236,0.08)' }}>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#F5F2EC]/65 mb-3">
                  Reflecting
                </div>
                <div
                  className="font-serif text-6xl sm:text-7xl text-[#C4440A] mb-2 tabular-nums"
                  data-testid="excavation-timer"
                >
                  {fmtTime(reflectionRemaining)}
                </div>
                <p className="text-[13px] text-[#F5F2EC]/55 italic mb-5">
                  Breathe. Sit with the question. Notice what your body says.
                </p>
                <div className="flex items-center justify-center gap-3">
                  <button
                    onClick={cancelReflection}
                    className="px-5 py-2.5 rounded-full border text-[13px] text-[#F5F2EC]/70 hover:text-[#F5F2EC] transition-colors"
                    style={{ borderColor: 'rgba(245,242,236,0.15)' }}
                    data-testid="excavation-cancel-btn"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={skipReflection}
                    className="px-5 py-2.5 rounded-full border text-[13px] text-[#F5F2EC]/70 hover:text-[#F5F2EC] transition-colors"
                    style={{ borderColor: 'rgba(245,242,236,0.15)' }}
                    data-testid="excavation-skip-btn"
                  >
                    Skip to writing
                  </button>
                </div>
              </div>
            )}

            {/* WRITING state — user writes in their physical journal */}
            {entryState === ENTRY_STATES.WRITING && (
              <div className="border-t pt-6 mt-2" style={{ borderColor: 'rgba(245,242,236,0.08)' }}>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#5DCAA5] mb-3">
                  Reflection complete
                </div>
                <p
                  className="font-serif text-xl text-[#F5F2EC] leading-snug mb-6"
                  data-testid="excavation-writing-instruction"
                >
                  Now write in your journal. Take your time. When you&apos;re done, come back and mark the day complete.
                </p>
                <button
                  onClick={markComplete}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-8 py-3 rounded-full font-medium transition-colors disabled:opacity-50"
                  style={{ background: CLAY, color: CREAM }}
                  data-testid="excavation-complete-btn"
                >
                  {saving ? 'Saving…' : (
                    <>
                      <Check className="w-4 h-4" /> Mark day complete
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Journey progress footer */}
          <div className="text-center text-[11px] text-[#F5F2EC]/65 tracking-[0.15em] uppercase">
            {progressPercent}% of the excavation complete
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExcavationWriting;
